import { debugWarn } from '../../utils/debug';
import { ledger17App } from './ledger17';
import { reinventarApp } from './reinventar';
import { saldo17App } from './saldo17';
import { sirktekApp } from './sirktek';
import { vardlyApp } from './vardly';
import type { AppConfig } from './types';

export type {
    AppConfig,
    AppTierConfig,
    BrandPalette,
    BrandPaletteColor,
    BrandSurfaces,
    BrandTokens,
    CurrencyCode,
    ThemeMode,
    TierMeteringStep,
    TierPrices,
} from './types';

/** Profile used when `VITE_APP_ID` is unset or unknown. */
export const DEFAULT_APP_ID = 'sirktek';

/**
 * Every deployable app profile. Adding an app means adding one module here plus
 * its static assets and translations — no component logic changes.
 */
export const APP_REGISTRY: Readonly<Record<string, AppConfig>> = {
    [sirktekApp.id]: sirktekApp,
    [ledger17App.id]: ledger17App,
    [vardlyApp.id]: vardlyApp,
    [saldo17App.id]: saldo17App,
    [reinventarApp.id]: reinventarApp,
};

export const APP_IDS: readonly string[] = Object.keys(APP_REGISTRY);

export const isKnownAppId = (appId: string): boolean =>
    Object.prototype.hasOwnProperty.call(APP_REGISTRY, appId);

/**
 * The profile this build was made for. App identity is immutable per build, so
 * callers can read this directly instead of threading it through props/context.
 */
export const getAppConfig = (): AppConfig => {
    const appId = (import.meta.env.VITE_APP_ID as string | undefined)?.trim();
    if (!appId) return APP_REGISTRY[DEFAULT_APP_ID];
    if (isKnownAppId(appId)) return APP_REGISTRY[appId];

    // Fail safe rather than render a broken UI; environment-validation surfaces
    // the same problem as a warning in development.
    debugWarn(
        `[appConfig] Unknown VITE_APP_ID "${appId}". Known apps: ${APP_IDS.join(', ')}. ` +
            `Falling back to "${DEFAULT_APP_ID}".`,
    );
    return APP_REGISTRY[DEFAULT_APP_ID];
};
